/**
 * The candidate funnel's one piece of arithmetic.
 *
 * ⚠️ `deriveFunnelInsights` lived here until Sep 23 2026 — busiest stage, positions without
 * candidates, positions in offer, mean candidates per position. It existed only for the KPI cards
 * on the standalone Open Positions page, which was deleted; the TA Cockpit already answers those
 * questions from the hiring plan, which knows how many HIRES a posting is for. Nothing imported it
 * afterwards, so it went rather than sitting here looking available.
 */
import type { FunnelStage } from '../types/openPositions';

/** Largest per-stage candidate count — what the funnel bars are scaled against. */
export function maxStageCandidates(stages: FunnelStage[]): number {
  return stages.reduce((max, s) => Math.max(max, s.candidates), 0);
}

/**
 * The three groups the funnel is read in (Theresa, Sep 29 2026).
 *
 * Fourteen stage bars in one list answered "which stage exactly" and never the question anyone
 * arrives with, which is where the mass sits: at the top of the funnel, in assessment, or at the
 * offer. So: everything before an assessment is one chart, every assessment round is a second, and
 * the offer gets a tile of its own because a count of three is not a bar.
 *
 * ⚠️ `other` IS A REAL GROUP AND IS RENDERED, not a bucket to drop things in. The match is on stage
 * NAMES, which are Lever's and change without telling us — a stage nobody classified would
 * otherwise vanish from a chart whose whole claim is that it accounts for every active candidate.
 *
 * ⚠️ AUTOMATION STAGES ARE THE ONE EXCEPTION, and they are an EXPLICIT LIST rather than a fallback.
 * "To be Archived" holds candidates a Lever automation is about to act on, not candidates anybody
 * is working (Theresa, Sep 29 2026: "this is only a stage for automations"). It is named in
 * `AUTOMATION_STAGES` and dropped before grouping, so the charts and the total they are measured
 * against both exclude it. The distinction from `other` matters: a stage we have decided is not
 * pipeline is dropped by NAME, while a stage nobody has classified yet is still shown — otherwise
 * the next automation stage somebody adds in Lever would disappear on its own.
 */
export type StageGroup = 'new' | 'assessment' | 'offer' | 'other';

/**
 * Matched on SUBSTRINGS of the lower-cased name, in this order, because Lever's stage names are
 * edited in Lever: "Case / Technical Interview" and "Final & Bar Raiser Interview" already carry
 * punctuation and an ampersand, and an exact list would have to be re-agreed every time somebody
 * renames one. Order matters — `interview` would otherwise claim the case and final rounds too.
 */
const GROUP_PATTERNS: { group: StageGroup; match: string[] }[] = [
  { group: 'offer', match: ['offer'] },
  {
    group: 'assessment',
    match: ['bryq', 'test', 'assessment', 'case', 'technical', 'final', 'bar raiser', 'interview'],
  },
  {
    group: 'new',
    match: ['new lead', 'reached out', 'responded', 'applicant', 'application', 'shortlist', 'parked', 'sourced'],
  },
];

/**
 * Stages that hold candidates for a machine rather than for a recruiter. Matched whole, lower-cased
 * and trimmed — not as substrings, because "archived" appears in ordinary stage names too.
 */
const AUTOMATION_STAGES = new Set(['to be archived']);

export function isAutomationStage(stageName: string): boolean {
  return AUTOMATION_STAGES.has(stageName.trim().toLowerCase());
}

export function groupOf(stageName: string): StageGroup {
  const name = stageName.toLowerCase();
  for (const { group, match } of GROUP_PATTERNS) {
    if (match.some((m) => name.includes(m))) return group;
  }
  return 'other';
}

export interface GroupedFunnel {
  group: StageGroup;
  stages: FunnelStage[];
  total: number;
}

export interface FunnelReading {
  groups: GroupedFunnel[];
  /** Every candidate the groups account for — automation stages are not in it. */
  total: number;
  /** What the automation stages held. Named on screen; never silently absorbed. */
  automationCandidates: number;
}

/**
 * The funnel as the section reads it: automation stages removed, the rest grouped.
 *
 * ⚠️ THE TOTAL IS REBUILT from the stages that survive rather than taken off the payload. The
 * payload's `totalCandidates` counts every stage including the automation one, so reusing it would
 * put a header over three charts that do not add up to it — the same mistake the radar's filter
 * avoids by rebuilding its own totals instead of reusing the whole-portfolio figures.
 *
 * Empty groups are dropped: a chart titled "where the new opportunities are" with nothing in it
 * says less than its absence does.
 */
export function readFunnel(stages: FunnelStage[]): FunnelReading {
  const automation = stages.filter((s) => isAutomationStage(s.name));
  const kept = stages.filter((s) => !isAutomationStage(s.name));
  const order: StageGroup[] = ['new', 'assessment', 'offer', 'other'];

  return {
    groups: order
      .map((group) => {
        const inGroup = kept.filter((s) => groupOf(s.name) === group);
        return { group, stages: inGroup, total: inGroup.reduce((sum, s) => sum + s.candidates, 0) };
      })
      .filter((g) => g.stages.length > 0),
    total: kept.reduce((sum, s) => sum + s.candidates, 0),
    automationCandidates: automation.reduce((sum, s) => sum + s.candidates, 0),
  };
}
